import React from 'react';
import { Button, Checkbox, DatePicker, Input, SelectPicker } from 'rsuite';
import { FormField, FormGrid, FormSection } from '../../common/FormModal';
import LanguageTabs, {
  getLanguagePanelId,
  getLanguageTabId,
  LanguageKey,
} from '../../common/LanguageTabs';
import RichTextEditor from '../../common/RichTextEditor';
import UploadImageModal from '../../common/UploadImageModal';
import { API_DOMAIN } from '../../config';
import BlogPostGallerySection from './BlogPostGallerySection';

type CategoryOption = {
  label: string;
  value: string;
};

type Props = {
  postId?: string;
  title: string;
  titleEn: string;
  titleDe: string;
  slug: string;
  categoryId: string;
  postedAt: Date | null;
  active: boolean;
  thumbnail: string;
  image: string;
  content: string;
  contentEn: string;
  contentDe: string;
  categories: CategoryOption[];
  setTitle: (value: string) => void;
  setTitleEn: (value: string) => void;
  setTitleDe: (value: string) => void;
  setSlug: (value: string) => void;
  setCategoryId: (value: string) => void;
  setPostedAt: (value: Date | null) => void;
  setActive: (value: boolean) => void;
  setThumbnail: (value: string) => void;
  setImage: (value: string) => void;
  setContent: (value: string) => void;
  setContentEn: (value: string) => void;
  setContentDe: (value: string) => void;
};

export default function BlogPostModalContent({
  postId,
  title,
  titleEn,
  titleDe,
  slug,
  categoryId,
  postedAt,
  active,
  thumbnail,
  image,
  content,
  contentEn,
  contentDe,
  categories,
  setTitle,
  setTitleEn,
  setTitleDe,
  setSlug,
  setCategoryId,
  setPostedAt,
  setActive,
  setThumbnail,
  setImage,
  setContent,
  setContentEn,
  setContentDe,
}: Props) {
  const [thumbnailUploadObj, setThumbnailUploadObj] = React.useState<null | {
    id?: string;
    image?: string;
  }>(null);
  const [imageUploadObj, setImageUploadObj] = React.useState<null | {
    id?: string;
    image?: string;
  }>(null);
  const [activeLanguage, setActiveLanguage] = React.useState<LanguageKey>('ua');
  const languageTabPrefix = 'blog-post-language';

  React.useEffect(() => {
    setActiveLanguage('ua');
  }, [postId]);

  const languageConfig: Record<
    LanguageKey,
    {
      title: string;
      setTitle: (value: string) => void;
      content: string;
      setContent: (value: string) => void;
    }
  > = {
    ua: {
      title,
      setTitle,
      content,
      setContent,
    },
    en: {
      title: titleEn,
      setTitle: setTitleEn,
      content: contentEn,
      setContent: setContentEn,
    },
    de: {
      title: titleDe,
      setTitle: setTitleDe,
      content: contentDe,
      setContent: setContentDe,
    },
  };

  const activeLanguageConfig = languageConfig[activeLanguage];

  const canUpload = Boolean(postId);

  const openThumbnailUpload = () => {
    if (!postId) {
      return;
    }
    setThumbnailUploadObj({ id: postId, image: thumbnail || undefined });
  };

  const openImageUpload = () => {
    if (!postId) {
      return;
    }
    setImageUploadObj({ id: postId, image: image || undefined });
  };

  const handleThumbnailModalClose = () => {
    setThumbnailUploadObj(null);
  };

  const handleImageModalClose = () => {
    setImageUploadObj(null);
  };

  return (
    <>
      <FormGrid>
        <FormField label="Статус">
          <Checkbox
            checked={active}
            onChange={(_value, checked) => {
              setActive(checked);
            }}
          >
            Активний
          </Checkbox>
        </FormField>
        <FormField label="Slug" required>
          <Input value={slug} onChange={setSlug} />
        </FormField>
        <FormField label="Категорія" required>
          <SelectPicker
            data={categories}
            value={categoryId || null}
            onChange={value => setCategoryId(value || '')}
            placeholder="Оберіть категорію"
            cleanable={false}
          />
        </FormField>
        <FormField label="Дата публікації">
          <DatePicker
            value={postedAt}
            onChange={setPostedAt}
            format="dd.MM.yyyy HH:mm"
            oneTap
          />
        </FormField>
        <FormField label="Thumbnail">
          {canUpload ? (
            <div className="form-modal__card form-modal__card--muted">
              <div className="form-modal__inline">
                <Button appearance="ghost" onClick={openThumbnailUpload}>
                  Завантажити thumbnail
                </Button>
                {thumbnail ? (
                  <Button
                    appearance="link"
                    onClick={() => window.open(thumbnail, '_blank')}
                  >
                    Переглянути
                  </Button>
                ) : null}
              </div>
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt="Thumbnail"
                  style={{
                    maxHeight: 140,
                    width: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <span className="form-modal__hint">Thumbnail не задано</span>
              )}
            </div>
          ) : (
            <span className="form-modal__hint">
              Завантаження доступне після створення поста.
            </span>
          )}
        </FormField>
        <FormField label="Image">
          {canUpload ? (
            <div className="form-modal__card form-modal__card--muted">
              <div className="form-modal__inline">
                <Button appearance="ghost" onClick={openImageUpload}>
                  Завантажити image
                </Button>
                {image ? (
                  <Button
                    appearance="link"
                    onClick={() => window.open(image, '_blank')}
                  >
                    Переглянути
                  </Button>
                ) : null}
              </div>
              {image ? (
                <img
                  src={image}
                  alt="Image"
                  style={{
                    maxHeight: 140,
                    width: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <span className="form-modal__hint">Image не задано</span>
              )}
            </div>
          ) : (
            <span className="form-modal__hint">
              Завантаження доступне після створення поста.
            </span>
          )}
        </FormField>
      </FormGrid>

      <FormSection title="Контент">
        <LanguageTabs
          activeLanguage={activeLanguage}
          onChange={setActiveLanguage}
          idPrefix={languageTabPrefix}
        />
        <div
          className="language-panel"
          role="tabpanel"
          id={getLanguagePanelId(languageTabPrefix, activeLanguage)}
          aria-labelledby={getLanguageTabId(languageTabPrefix, activeLanguage)}
        >
          <div className="language-fields">
            <FormField label="Заголовок" required={activeLanguage === 'ua'}>
              <Input
                value={activeLanguageConfig.title || ''}
                onChange={activeLanguageConfig.setTitle}
              />
            </FormField>
            <FormField label="Текст" required={activeLanguage === 'ua'}>
              <RichTextEditor
                value={activeLanguageConfig.content || ''}
                onChange={activeLanguageConfig.setContent}
                placeholder="Напишіть текст посту..."
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      <FormSection title="Галерея">
        {postId ? (
          <BlogPostGallerySection postId={postId} />
        ) : (
          <span className="form-modal__hint">
            Доступно після створення поста.
          </span>
        )}
      </FormSection>

      <UploadImageModal
        title="Редагувати thumbnail поста"
        actionUrl={`${API_DOMAIN}/api/blog-posts/${postId}/thumbnail`}
        object={thumbnailUploadObj}
        handleModalClose={handleThumbnailModalClose}
        onUploaded={payload => {
          const nextThumbnail = payload.thumbnail;
          if (typeof nextThumbnail === 'string') {
            setThumbnail(nextThumbnail);
          }
        }}
      />
      <UploadImageModal
        title="Редагувати зображення поста"
        actionUrl={`${API_DOMAIN}/api/blog-posts/${postId}/image`}
        object={imageUploadObj}
        handleModalClose={handleImageModalClose}
        onUploaded={payload => {
          const nextImage = payload.image;
          if (typeof nextImage === 'string') {
            setImage(nextImage);
          }
        }}
      />
    </>
  );
}
